import { Component, OnInit, OnChanges, SimpleChanges, OnDestroy, Input, ViewChild, ElementRef } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

/* =======================================
Ref: https://chartjs-plugin-datalabels.netlify.com/guide/getting-started.html#integration

This plugin registers itself globally,
meaning that once imported, all charts will display labels.

In case you want it enabled only for a few charts,
you first need to unregister it globally:
========================================= */

Chart.plugins.unregister(ChartDataLabels);

interface ScaleLabel {
  display: boolean;
  labelString: string;
}

@Component({
  selector: 'vrm-chart',

  // NOTE: The canvas tag cannot be in a non-html tag such as ng-content
  // hence why it's wrapped with the div at this level!
  template: `
    <div fxLayoutAlign="center" style="position: relative; height: 40vh">
      <canvas #chartCanvas></canvas>
    </div>
  `,
  styles: [
    `
      canvas {
        margin-top: 10px;
        margin-bottom: 20px;
        width: 100% !important; /* <-- necessary for canvas to be responsive */
      }
    `,
  ],
})
export class VrmChartJsComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('chartCanvas', { static: true })
  chartElement: ElementRef;

  @Input() chartId: string | number;
  @Input() chartData: any;

  constructor(private translate: TranslateService) {
    Chart.plugins.register(ChartDataLabels);
  }

  // LIFECYCLE
  ngOnInit() {
    this.renderCharts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData']) {
      this.renderCharts();
    }
  }

  // FUNCTIONS
  renderCharts(): void {
    this.translate.get('CHART').subscribe({
      next: (translation: object) => {
        let renderChart: any;
        const options = this.chartData.options;
        const yAxisScales: Array<object> = options && options.scales.yAxes ? options.scales.yAxes : null;

        delete this.chartData.plugins;

        // Attempt title text translation
        if (options.title.translatedText) {
          options.title.text = options.title.translatedText;
        } else {
          if (translation[options.title.text]) {
            options.title.text = translation[options.title.text];
          }
        }

        // Translate dataset labels
        if (this.chartData.data && this.chartData.data.datasets) {
          this.chartData.data.datasets.forEach((dataItem: { label: string }) => {
            if (translation[dataItem.label]) {
              dataItem.label = translation[dataItem.label];
            }
          });
        }

        // y-axis label translation
        if (yAxisScales && yAxisScales.length) {
          yAxisScales.forEach((scale: { scaleLabel: ScaleLabel }, i: number) => {
            const scaleLabel = scale.scaleLabel;

            if (scaleLabel && scaleLabel.display && scaleLabel.labelString) {
              const translatedScaleLabel = translation[scaleLabel.labelString];

              if (translatedScaleLabel) {
                options.scales.yAxes[i].scaleLabel.labelString = translatedScaleLabel;
              }
            }
          });
        }

        this.chartData.options = options;

        if (this.chartData.type && this.chartData.type.toLowerCase() === 'bar') {
          options.scales.yAxes[0].ticks = {
            min: 0,
            userCallback: (value: number, index: number, values: number) => {
              return value.toFixed(2);
            },
          };
          this.chartData.options = options;

          renderChart = new Chart(this.chartElement.nativeElement, {
            plugins: [ChartDataLabels],
            ...this.chartData,
          });
        } else {
          renderChart = new Chart(this.chartElement.nativeElement, { plugins: [], ...this.chartData });
          Chart.plugins.unregister(ChartDataLabels);
        }
      },
    });
  }

  ngOnDestroy() {
    Chart.plugins.unregister(ChartDataLabels);
  }
}
